/**
 * RBAC Context Provider (v2.0)
 * 
 * Provides comprehensive RBAC state throughout the application including:
 * - User context with permissions, roles, groups, constraints
 * - Permission checking helpers
 * - Approval policy awareness
 * - Constraint validation
 * 
 * @module RBACContext
 */

'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  getUserContext,
  getUserPermissions,
  getUserByExternalId,
  getUserRoles,
  getAccountRoles,
  getRoleFull,
  listRoles,
  hasPermission as hasPermissionFn,
  hasAnyPermission as hasAnyPermissionFn,
  matchesPermission,
  checkConstraints,
  RBACApiError,
} from '@/services/rbacApi';
import { getUserDetails } from '@/services/userLoginApi';
import type {
  UserContext,
  RoleConstraints,
  UserContextRole,
} from '@/types/rbac';

// ============================================
// Types
// ============================================

interface ConstraintContext {
  channel?: string;
  brand_id?: number;
  location_id?: number;
  spend_amount?: number;
}

interface RBACContextType {
  // State
  userContext: UserContext | null;
  isLoading: boolean;
  error: string | null;
  
  // Current scope
  currentAccountId: number | null;
  defaultAccountId: number | null;
  currentOrgId: number | null;
  selectedSubAccountId: number | null;
  selectedSubTenantMemberExternalUserId: number | null;
  selectedSubTenantMemberRbacUserId: number | null;
  /**
   * Account id used to resolve member_user_ids for listings: sub-tenant picker and/or account
   * switcher when current account ≠ primary (`effectiveScopedAccountId`). Null when not aggregating.
   */
  effectiveListingSubAccountId: number | null;

  // Root org context (org owner selected "Organization" in the switcher)
  isRootContextActive: boolean;
  setIsRootContextActive: (v: boolean) => void;

  // Actions
  setCurrentAccountId: (accountId: number) => void;
  setSelectedSubAccountId: (id: number | null) => void;
  setSelectedSubTenantMemberExternalUserId: (id: number | null, rbacUserId?: number | null) => void;
  refreshContext: (opts?: { silent?: boolean }) => Promise<void>;
  
  // Permission helpers
  hasPermission: (required: string | string[]) => boolean;
  hasAnyPermission: (required: string[]) => boolean;
  canAccess: (permission: string, context?: ConstraintContext) => boolean;
  
  // Role helpers
  hasRole: (roleCode: string) => boolean;
  getRoles: () => UserContextRole[];
  
  // Group helpers
  isInGroup: (groupName: string) => boolean;
  getGroups: () => string[];
  
  // Constraint helpers
  getConstraints: () => RoleConstraints;
  checkConstraintAccess: (context: ConstraintContext) => { allowed: boolean; reason?: string };
  
  // Approval helpers
  requiresApprovalForPublish: () => boolean;
  requiresApprovalForSpend: (amount: number) => boolean;
  
  // Entitlement helpers (from subscription)
  hasEntitlement: (feature: string) => boolean;
  
  // Computed values
  isOrgOwner: boolean;
  isAccountAdmin: boolean;
  permissions: string[];
  /** True while sub-tenant scoped permissions are still loading (avoid false denials in route guard). */
  isSubTenantScopeLoading: boolean;
}

interface RBACProviderProps {
  children: ReactNode;
  defaultAccountId?: number;
}

// ============================================
// Context
// ============================================

const RBACContext = createContext<RBACContextType | null>(null);

const STORAGE_SELECTED_SUBTENANT = 'rbac_selected_subtenant_id';
const STORAGE_SELECTED_SUBTENANT_MEMBER = 'rbac_selected_subtenant_member_external_user_id';
const STORAGE_SELECTED_SUBTENANT_MEMBER_RBAC_USER = 'rbac_selected_subtenant_member_rbac_user_id';
const STORAGE_OWNER_ACCOUNT_ID = 'rbac_owner_account_id';
const STORAGE_ROOT_CONTEXT_ACTIVE = 'rbac_root_context_active';

function readStoredSubtenantId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_SELECTED_SUBTENANT);
  if (raw == null || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function readStoredSubtenantMemberExternalUserId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_SELECTED_SUBTENANT_MEMBER);
  if (raw == null || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function readStoredSubtenantMemberRbacUserId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_SELECTED_SUBTENANT_MEMBER_RBAC_USER);
  if (raw == null || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function readStoredRootContextActive(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_ROOT_CONTEXT_ACTIVE) === '1';
}

/** Last known primary (owner) org account id — used to reset sub-tenant scope when context omits account_id. */
export function readStoredOwnerAccountId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_OWNER_ACCOUNT_ID);
  if (raw == null || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Hook to access RBAC context
 */
export function useRBAC(): RBACContextType {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within RBACProvider');
  }
  return context;
}

/**
 * Hook to access RBAC context (optional - returns null if not in provider)
 */
export function useRBACOptional(): RBACContextType | null {
  return useContext(RBACContext);
}

// ============================================
// Provider Component
// ============================================

export function RBACProvider({
  children,
  defaultAccountId,
}: RBACProviderProps) {
  const [userContext, setUserContext] = useState<UserContext | null>(null);
  const [currentAccountId, setCurrentAccountIdState] = useState<number | null>(defaultAccountId || null);
  const [defaultAccountIdState, setDefaultAccountIdState] = useState<number | null>(null);
  const [selectedSubAccountId, setSelectedSubAccountIdState] = useState<number | null>(() =>
    readStoredSubtenantId()
  );
  const [selectedSubTenantMemberExternalUserId, setSelectedSubTenantMemberExternalUserIdState] =
    useState<number | null>(() => readStoredSubtenantMemberExternalUserId());
  const [selectedSubTenantMemberRbacUserId, setSelectedSubTenantMemberRbacUserIdState] =
    useState<number | null>(() => readStoredSubtenantMemberRbacUserId());
  const [isRootContextActive, setIsRootContextActiveState] = useState<boolean>(() =>
    readStoredRootContextActive()
  );
  const [scopedPermissions, setScopedPermissions] = useState<string[] | null>(null);
  const scopedPermissionsForAccountRef = useRef<{
    accountId: number;
    memberExternalUserId: number | null;
    permissions: string[];
  } | null>(null);
  const lastScopedFetchAccountRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const effectiveScopedAccountId = useMemo(() => {
    if (selectedSubAccountId) return selectedSubAccountId;
    // Org owner viewing as a specific member under root org context: scope permissions to
    // that member's actual role on the root account instead of bypassing via the owner's
    // full ["*"] set. Data listing already scopes to the member via effectiveListingSubAccountId;
    // this makes the permission check (nav, PermissionGate, route guard) match what's shown.
    // Except when the member picked is the owner themself: an org owner's access comes from
    // is_org_owner, not a UserRole row, so scoping to "their role on the root account" finds no
    // roles and yields zero permissions (empty sidebar). Viewing as yourself keeps owner access;
    // data listing still narrows to the owner's own records via the selected member id.
    if (
      isRootContextActive &&
      userContext?.is_org_owner &&
      selectedSubTenantMemberExternalUserId != null &&
      String(selectedSubTenantMemberExternalUserId) !== String(userContext.user_id)
    ) {
      // currentAccountId is set to the real root account id by the "Organization" selector
      // (resolved via listAllAccounts, independent of /me/context) — prefer it. A pure org-owner's
      // /me/context response can have account: null (they're not tied to one account), so
      // defaultAccountIdState/readStoredOwnerAccountId() may never get populated; keep them only
      // as a fallback.
      const rootId = currentAccountId ?? defaultAccountIdState ?? readStoredOwnerAccountId();
      if (rootId != null) return rootId;
    }
    if (defaultAccountIdState && currentAccountId && currentAccountId !== defaultAccountIdState) {
      return currentAccountId;
    }
    // Non-org-owner members are always scoped to their own account so they see
    // all subtenant data (via memberUserIds) not just their own records.
    if (userContext && !userContext.is_org_owner && userContext.account_id) {
      return userContext.account_id;
    }
    return null;
  }, [
    selectedSubAccountId,
    isRootContextActive,
    userContext?.is_org_owner,
    userContext?.user_id,
    selectedSubTenantMemberExternalUserId,
    defaultAccountIdState,
    currentAccountId,
    userContext?.account_id,
  ]);

  // Monotonic id of the latest refreshContext() call (also bumped on impersonation reset). Only
  // the latest call may apply its result: an older in-flight /me/context — e.g. a silent
  // visibilitychange refresh sent with the admin's cookie just before impersonation started —
  // would otherwise land after the reset and overwrite the impersonated user's context with the
  // admin's, leaving a mismatched account scope (empty sidebar, dashboard stuck on its loader).
  const refreshGenerationRef = useRef(0);

  // Fetch user context
  const refreshContext = useCallback(async (opts?: { silent?: boolean }) => {
    const generation = ++refreshGenerationRef.current;
    const isStale = () => generation !== refreshGenerationRef.current;
    // Bust the in-memory scoped-permission cache so role changes are picked up.
    scopedPermissionsForAccountRef.current = null;
    lastScopedFetchAccountRef.current = null;
    try {
      if (!opts?.silent) setIsLoading(true);
      setError(null);
      // Always load org-level identity from /me/context. Passing the active sub-tenant account_id
      // returns scoped slices where is_org_owner may be false, which incorrectly clears sub-tenant selection.
      const context = await getUserContext(undefined);
      if (isStale()) return;
      setUserContext(context);
      // Primary org account id (normalizeMeContext uses 0 when missing; treat as invalid).
      let primaryAccountId =
        Number.isFinite(context.account_id) && context.account_id > 0 ? context.account_id : null;

      // Some org owners have no account bound in rbac-svc's /me/context (account: null,
      // normalized to 0 above), even though the user-login service's own profile knows
      // their real home account. Without this, defaultAccountId/currentAccountId stay
      // null forever for that user, which breaks "reset to owner" and the
      // X-Active-Account-Id header on aggregate list calls (e.g. digital-ads analytics)
      // in their default, nothing-selected context.
      if (primaryAccountId == null && context.is_org_owner) {
        try {
          const details = await getUserDetails();
          const fallbackAccountId = Number(details.account_id);
          if (Number.isFinite(fallbackAccountId) && fallbackAccountId > 0) {
            primaryAccountId = fallbackAccountId;
          }
        } catch {
          // Best-effort fallback only — ignore failures here.
        }
        if (isStale()) return;
      }

      if (primaryAccountId != null) {
        setDefaultAccountIdState(primaryAccountId);
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_OWNER_ACCOUNT_ID, String(primaryAccountId));
        }
      }

      // Update account ID from context if not set
      if (!currentAccountId && primaryAccountId != null) {
        setCurrentAccountIdState(primaryAccountId);
      }
    } catch (err) {
      if (isStale()) return;
      if (err instanceof RBACApiError) {
        setError(err.message);
      } else {
        setError('Failed to fetch user context');
      }
      setUserContext(null);
    } finally {
      // A superseded call leaves isLoading to the latest one. The latest call clears it even when
      // silent, since a non-silent call it superseded may have set it.
      if (!isStale()) setIsLoading(false);
    }
  }, [currentAccountId, defaultAccountIdState]);

  // Set current account and refresh context
  const setCurrentAccountId = useCallback(
    (accountId: number) => {
      setCurrentAccountIdState((prev) => {
        if (
          typeof window !== 'undefined' &&
          defaultAccountIdState != null &&
          defaultAccountIdState > 0 &&
          prev === defaultAccountIdState &&
          accountId !== defaultAccountIdState
        ) {
          localStorage.setItem(STORAGE_OWNER_ACCOUNT_ID, String(defaultAccountIdState));
        }
        return accountId;
      });
      if (typeof window !== 'undefined') {
        localStorage.setItem('rbac_current_account_id', String(accountId));
      }
    },
    [defaultAccountIdState]
  );

  const setIsRootContextActive = useCallback((v: boolean) => {
    setIsRootContextActiveState(v);
    if (typeof window !== 'undefined') {
      if (v) {
        localStorage.setItem(STORAGE_ROOT_CONTEXT_ACTIVE, '1');
      } else {
        localStorage.removeItem(STORAGE_ROOT_CONTEXT_ACTIVE);
      }
    }
  }, []);

  const setSelectedSubAccountId = useCallback((id: number | null) => {
    if (id != null) {
      // Selecting a specific sub-account exits root org context
      setIsRootContextActiveState(false);
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_ROOT_CONTEXT_ACTIVE);
      }
    }
    setSelectedSubAccountIdState(id);
    setSelectedSubTenantMemberExternalUserIdState(null);
    setSelectedSubTenantMemberRbacUserIdState(null);
    if (typeof window !== 'undefined') {
      if (id == null) {
        localStorage.removeItem(STORAGE_SELECTED_SUBTENANT);
        localStorage.removeItem(STORAGE_SELECTED_SUBTENANT_MEMBER);
        localStorage.removeItem(STORAGE_SELECTED_SUBTENANT_MEMBER_RBAC_USER);
      } else {
        localStorage.setItem(STORAGE_SELECTED_SUBTENANT, String(id));
        localStorage.removeItem(STORAGE_SELECTED_SUBTENANT_MEMBER);
        localStorage.removeItem(STORAGE_SELECTED_SUBTENANT_MEMBER_RBAC_USER);
      }
    }
  }, []);

  const setSelectedSubTenantMemberExternalUserId = useCallback((id: number | null, rbacUserId?: number | null) => {
    setSelectedSubTenantMemberExternalUserIdState(id);
    setSelectedSubTenantMemberRbacUserIdState(rbacUserId ?? null);
    if (typeof window !== 'undefined') {
      if (id == null) {
        localStorage.removeItem(STORAGE_SELECTED_SUBTENANT_MEMBER);
        localStorage.removeItem(STORAGE_SELECTED_SUBTENANT_MEMBER_RBAC_USER);
      } else {
        localStorage.setItem(STORAGE_SELECTED_SUBTENANT_MEMBER, String(id));
        if (rbacUserId != null) {
          localStorage.setItem(STORAGE_SELECTED_SUBTENANT_MEMBER_RBAC_USER, String(rbacUserId));
        } else {
          localStorage.removeItem(STORAGE_SELECTED_SUBTENANT_MEMBER_RBAC_USER);
        }
      }
    }
  }, []);

  // For non-owners: auto-scope selectedSubAccountId to their assigned account so
  // X-Active-Account-Id is always sent (root-account members → root_account_id,
  // sub-account members → their sub_account_id). Never let a non-owner hold a
  // selectedSubAccountId that doesn't match their own account.
  const userRoleCodesKey =
    userContext?.roles?.map((r) => r.role_code).filter(Boolean).sort().join('\0') ?? '';
  useEffect(() => {
    if (isLoading || !userContext) return;
    const isOwner =
      Boolean(userContext.is_org_owner) ||
      Boolean(userContext.roles?.some((r) => r.role_code === 'org_owner'));
    if (isOwner) return;

    const accountId = userContext.account_id ?? null;
    // Only update if current selection doesn't match the user's own account.
    if (selectedSubAccountId !== accountId) {
      if (accountId != null) {
        setSelectedSubAccountIdState(accountId);
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_SELECTED_SUBTENANT, String(accountId));
        }
      } else {
        setSelectedSubAccountIdState(null);
        if (typeof window !== 'undefined') {
          localStorage.removeItem(STORAGE_SELECTED_SUBTENANT);
        }
      }
      setSelectedSubTenantMemberExternalUserIdState(null);
      setSelectedSubTenantMemberRbacUserIdState(null);
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_SELECTED_SUBTENANT_MEMBER);
        localStorage.removeItem(STORAGE_SELECTED_SUBTENANT_MEMBER_RBAC_USER);
      }
    }

    const def = accountId ?? defaultAccountIdState ?? null;
    if (def != null) {
      setCurrentAccountIdState(def);
      if (typeof window !== 'undefined') {
        localStorage.setItem('rbac_current_account_id', String(def));
      }
    }
  }, [
    isLoading,
    userContext?.user_id,
    userContext?.is_org_owner,
    userContext?.account_id,
    userRoleCodesKey,
    defaultAccountIdState,
    selectedSubAccountId,
  ]);

  // Initial load
  useEffect(() => {
    // Try to restore account ID from localStorage
    if (typeof window !== 'undefined' && !currentAccountId) {
      const stored = localStorage.getItem('rbac_current_account_id');
      if (stored) {
        setCurrentAccountIdState(parseInt(stored, 10));
      }
    }
    // Reconcile: if user chose a sub-tenant before, active account in storage should match
    const storedSub = readStoredSubtenantId();
    if (typeof window !== 'undefined' && storedSub != null) {
      const acc = localStorage.getItem('rbac_current_account_id');
      if (!acc || parseInt(acc, 10) !== storedSub) {
        localStorage.setItem('rbac_current_account_id', String(storedSub));
        setCurrentAccountIdState(storedSub);
      }
    }
    refreshContext();
  }, []);

  // Re-fetch permissions when the tab becomes visible again so role changes made
  // by an admin while the member was away are reflected without a manual reload.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshContext({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshContext]);

  // Refresh when account changes (real subsequent switches only — the initial mount-time
  // value is already covered by the "Initial load" effect's unconditional refreshContext()
  // call above; without this guard, any dashboard load that already knows an account id
  // fired the exact same unscoped GET /api/v1/me/context twice back to back on every mount,
  // doubling everything downstream that depends on userContext, e.g. the sub-account selector).
  const isFirstAccountEffectRun = useRef(true);
  // Set right before the identity-swap effect below calls refreshContext() itself, so that
  // call's own currentAccountId update (null -> the new user's account) doesn't also trigger
  // this effect and fire a second, redundant /me/context fetch.
  const suppressNextAccountEffectRef = useRef(false);
  useEffect(() => {
    if (isFirstAccountEffectRun.current) {
      isFirstAccountEffectRun.current = false;
      return;
    }
    if (suppressNextAccountEffectRef.current) {
      suppressNextAccountEffectRef.current = false;
      return;
    }
    if (currentAccountId) {
      refreshContext();
    }
  }, [currentAccountId]);

  // Impersonation start/stop swaps the active identity without a page reload (see
  // broadcastImpersonationState in ImpersonationContext.js). The admin's org/account/permission
  // context does not carry over to the impersonated user (or back), so every piece of scoped
  // state here has to be invalidated and re-fetched from scratch — mirroring what a fresh page
  // load would compute. `identityResetTick` decouples the reset from the refetch: state resets
  // are batched into one render, and only once that render lands (so currentAccountId/
  // defaultAccountIdState really are null) do we call refreshContext(), avoiding the stale-closure
  // trap of calling it in the same tick as the resets.
  const [identityResetTick, setIdentityResetTick] = useState(0);
  useEffect(() => {
    const resetScope = () => {
      // Set synchronously here (not left to refreshContext()'s own effect) so it lands in the
      // SAME batched commit as the rest of this reset. React runs effects child-before-parent,
      // so consumers deep in the tree (e.g. useDashboardData) run their effects before this
      // component's own reset-tick effect below gets a chance to call refreshContext() and flip
      // isLoading — a consumer gating on rbac.isLoading would still see the old (stale) `false`
      // in that first commit otherwise, and could fire a fetch with accountId still null.
      // Invalidate any /me/context still in flight for the previous identity.
      refreshGenerationRef.current += 1;
      setIsLoading(true);
      setUserContext(null);
      setCurrentAccountIdState(null);
      setDefaultAccountIdState(null);
      setSelectedSubAccountIdState(null);
      setSelectedSubTenantMemberExternalUserIdState(null);
      setSelectedSubTenantMemberRbacUserIdState(null);
      setIsRootContextActiveState(false);
      setScopedPermissions(null);
      scopedPermissionsForAccountRef.current = null;
      lastScopedFetchAccountRef.current = null;
      setIdentityResetTick((t) => t + 1);
    };
    const handleCustom = (e: Event) => {
      const active = (e as CustomEvent<{ active?: boolean }>).detail?.active;
      if (active === undefined) return; // guard against unrelated CustomEvents on the same name
      resetScope();
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'impersonation' && e.key !== null) return;
      resetScope();
    };
    window.addEventListener('impersonation-change', handleCustom);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('impersonation-change', handleCustom);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const isFirstResetTick = useRef(true);
  useEffect(() => {
    if (isFirstResetTick.current) {
      isFirstResetTick.current = false;
      return;
    }
    suppressNextAccountEffectRef.current = true;
    refreshContext();
    // Safety net: if refreshContext() fails, it never updates currentAccountId, so the
    // suppress flag above would otherwise never get consumed — silently skipping the next
    // real (unrelated) account switch. Bound how long it can stay set.
    const clearSuppressTimer = setTimeout(() => {
      suppressNextAccountEffectRef.current = false;
    }, 10000);
    return () => clearTimeout(clearSuppressTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityResetTick]);

  // When a sub-tenant is selected, load effective permissions scoped to it.
  // Fallback to account-level context permissions if scoped fetch fails.
  useEffect(() => {
    if (!userContext?.user_id || !effectiveScopedAccountId) {
      lastScopedFetchAccountRef.current = null;
      setScopedPermissions(null);
      return;
    }
    let cancelled = false;
    const scopeId = effectiveScopedAccountId;
    const scopeKey = `${scopeId}:${selectedSubTenantMemberExternalUserId ?? 'all'}`;
    if (lastScopedFetchAccountRef.current !== scopeKey) {
      lastScopedFetchAccountRef.current = scopeKey;
      setScopedPermissions(null);
      scopedPermissionsForAccountRef.current = null;
    }
    (async () => {
      try {
        if (selectedSubTenantMemberExternalUserId != null) {
          let rbacUserId = selectedSubTenantMemberRbacUserId;
          if (rbacUserId == null) {
            const member = await getUserByExternalId(selectedSubTenantMemberExternalUserId);
            rbacUserId = Number(member?.user?.id);
          }

          if (!Number.isFinite(rbacUserId)) {
            if (!cancelled) {
              scopedPermissionsForAccountRef.current = {
                accountId: scopeId,
                memberExternalUserId: selectedSubTenantMemberExternalUserId,
                permissions: [],
              };
              setScopedPermissions([]);
            }
            return;
          }

          const roleResponses = await Promise.all([
            getUserRoles(rbacUserId, 'account', scopeId).catch((e) => { console.warn('[RBAC DEBUG] getUserRoles account failed', e); return { roles: [] }; }),
            getUserRoles(rbacUserId, 'sub_account', scopeId).catch((e) => { console.warn('[RBAC DEBUG] getUserRoles sub_account failed', e); return { roles: [] }; }),
          ]);
          const explicitRoleIds = Array.from(
            new Set(
              roleResponses
                .flatMap((res: any) => res?.roles || [])
                .map((role: any) => Number(role?.role_id))
                .filter((id: number) => Number.isFinite(id))
            )
          );

          if (explicitRoleIds.length === 0) {
            if (!cancelled) {
              scopedPermissionsForAccountRef.current = {
                accountId: scopeId,
                memberExternalUserId: selectedSubTenantMemberExternalUserId,
                permissions: [],
              };
              setScopedPermissions([]);
            }
            return;
          }

          const roleDetails = await Promise.all(
            explicitRoleIds.map((roleId: number) => getRoleFull(roleId).catch(() => null))
          );
          const explicitPermissions = Array.from(
            new Set(
              roleDetails
                .flatMap((role: any) => role?.permissions || [])
                .map((p: any) => p?.code)
                .filter((code: string | undefined): code is string => typeof code === 'string' && code.length > 0)
            )
          );

          if (!cancelled) {
            scopedPermissionsForAccountRef.current = {
              accountId: scopeId,
              memberExternalUserId: selectedSubTenantMemberExternalUserId,
              permissions: explicitPermissions,
            };
            setScopedPermissions(explicitPermissions);
          }
          return;
        }

        // In aggregate sub-tenant mode, keep the historical account-scope behavior.
        try {
          const accountScoped = await getUserPermissions(userContext.user_id, 'account', effectiveScopedAccountId);
          const accountPerms = Array.isArray(accountScoped?.permissions) ? accountScoped.permissions : [];
          if (accountPerms.length > 0) {
            if (!cancelled) {
              scopedPermissionsForAccountRef.current = {
                accountId: scopeId,
                memberExternalUserId: null,
                permissions: accountPerms,
              };
              setScopedPermissions(accountPerms);
            }
            return;
          }

          // Fallback: derive permissions from roles assigned to this sub-tenant account.
          const accountRoles = await getAccountRoles(effectiveScopedAccountId);
          const accountRoleList = accountRoles?.roles || [];

          // 1) If backend already returns permissions on /accounts/{id}/roles, use them directly.
          const embedded = Array.from(
            new Set(
              accountRoleList
                .flatMap((r: any) => r?.permissions || [])
                .map((p: any) => p?.code)
                .filter((code: string | undefined): code is string => typeof code === 'string' && code.length > 0)
            )
          );
          if (embedded.length > 0) {
            if (!cancelled) {
              scopedPermissionsForAccountRef.current = {
                accountId: scopeId,
                memberExternalUserId: null,
                permissions: embedded,
              };
              setScopedPermissions(embedded);
            }
            return;
          }

          let roleIds = accountRoleList
            .map((r: any) => Number(r?.id))
            .filter((id: number) => Number.isFinite(id));

          // 2) If only role codes are returned, resolve ids via listRoles(org).
          if (roleIds.length === 0) {
            const roleCodes = accountRoleList
              .map((r: any) => String(r?.code || r?.role_code || '').trim())
              .filter((code: string) => code.length > 0);

            if (roleCodes.length > 0 && userContext.organization_id) {
              const allRoles = await listRoles({ orgId: userContext.organization_id, includeSystem: true });
              const idByCode = new Map((allRoles?.roles || []).map((r: any) => [String(r.code), Number(r.id)]));
              roleIds = roleCodes
                .map((code: string) => idByCode.get(code))
                .filter((id: number | undefined): id is number => Number.isFinite(id));
            }
          }

          if (roleIds.length > 0) {
            const roleDetails = await Promise.all(
              roleIds.map((roleId: number) =>
                getRoleFull(roleId).catch(() => null)
              )
            );
            const derived = Array.from(
              new Set(
                roleDetails
                  .flatMap((role: any) => role?.permissions || [])
                  .map((p: any) => p?.code)
                  .filter((code: string | undefined): code is string => typeof code === 'string' && code.length > 0)
              )
            );
            if (!cancelled) {
              scopedPermissionsForAccountRef.current = {
                accountId: scopeId,
                memberExternalUserId: null,
                permissions: derived,
              };
              setScopedPermissions(derived);
            }
            return;
          }

          if (!cancelled) {
            scopedPermissionsForAccountRef.current = {
              accountId: scopeId,
              memberExternalUserId: null,
              permissions: [],
            };
            setScopedPermissions([]);
          }
          return;
        } catch {
          const subAccountScoped = await getUserPermissions(userContext.user_id, 'sub_account', effectiveScopedAccountId);
          if (!cancelled) {
            const p = Array.isArray(subAccountScoped?.permissions) ? subAccountScoped.permissions : [];
            scopedPermissionsForAccountRef.current = {
              accountId: scopeId,
              memberExternalUserId: null,
              permissions: p,
            };
            setScopedPermissions(p);
          }
        }
      } catch (err) {
        console.error('Failed to load sub-account scoped permissions:', err);
        if (!cancelled) setScopedPermissions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    userContext?.user_id,
    effectiveScopedAccountId,
    userContext?.organization_id,
    selectedSubTenantMemberExternalUserId,
    selectedSubTenantMemberRbacUserId,
  ]);

  // ============================================
  // Permission Helpers
  // ============================================

  // When a sub-tenant scope is active, never fall back to userContext.permissions for org-owners —
  // that would briefly flash the full ["*"] set when the owner switches to a member's limited view.
  // For non-owner members, the context already scopes permissions to their account, so we CAN fall
  // back to userContext.permissions while the scoped fetch is in flight (prevents empty-nav flash).
  const permissions = useMemo(() => {
    if (!effectiveScopedAccountId) {
      return userContext?.permissions ?? [];
    }
    if (scopedPermissions !== null) {
      return scopedPermissions;
    }
    const cached = scopedPermissionsForAccountRef.current;
    if (
      cached?.accountId === effectiveScopedAccountId &&
      cached.memberExternalUserId === (selectedSubTenantMemberExternalUserId ?? null)
    ) {
      return cached.permissions;
    }
    // Non-owners: context permissions are already account-scoped; safe to use as interim fallback.
    // Owners: return [] so a mid-flight switch doesn't flash full permissions into a limited view.
    if (!userContext?.is_org_owner) {
      return userContext?.permissions ?? [];
    }
    return [];
  }, [
    effectiveScopedAccountId,
    scopedPermissions,
    selectedSubTenantMemberExternalUserId,
    userContext?.permissions,
    userContext?.is_org_owner,
  ]);

  const hasPermission = useCallback(
    (required: string | string[]) => {
      // In selected sub-tenant mode, permissions must be enforced strictly from that scope.
      // No org-level owner/admin bypass here.
      if (effectiveScopedAccountId) {
        return hasPermissionFn(permissions, required);
      }
      // Org owner: backend sends permissions: ["*"] or is_org_owner; owner may have no UserRole row for org_admin/org_owner
      if (userContext?.is_org_owner) return true;
      const elevated = userContext?.roles?.some(
        (r) => r.role_code === 'org_admin' || r.role_code === 'org_owner'
      );
      if (elevated) return true;
      return hasPermissionFn(permissions, required);
    },
    [permissions, userContext, effectiveScopedAccountId]
  );

  const hasAnyPermission = useCallback(
    (required: string[]) => {
      if (effectiveScopedAccountId) {
        return hasAnyPermissionFn(permissions, required);
      }
      if (userContext?.is_org_owner) return true;
      const elevated = userContext?.roles?.some(
        (r) => r.role_code === 'org_admin' || r.role_code === 'org_owner'
      );
      if (elevated) return true;
      return hasAnyPermissionFn(permissions, required);
    },
    [permissions, userContext, effectiveScopedAccountId]
  );

  const canAccess = useCallback(
    (permission: string, context?: ConstraintContext) => {
      if (effectiveScopedAccountId) {
        const hasPermsScoped = permissions.some((p) => matchesPermission(p, permission) || matchesPermission(permission, p));
        if (!hasPermsScoped) return false;
        if (context && userContext?.aggregated_constraints) {
          const result = checkConstraints(userContext.aggregated_constraints, context);
          return result.allowed;
        }
        return true;
      }
      if (userContext?.is_org_owner) return true;
      const elevated = userContext?.roles?.some(
        (r) => r.role_code === 'org_admin' || r.role_code === 'org_owner'
      );
      if (elevated) return true;
      // Check permission first (includes * via matchesPermission(permission, '*'))
      const hasPerms = permissions.some((p) => matchesPermission(p, permission) || matchesPermission(permission, p));
      if (!hasPerms) return false;

      // Check constraints if context provided
      if (context && userContext?.aggregated_constraints) {
        const result = checkConstraints(userContext.aggregated_constraints, context);
        return result.allowed;
      }

      return true;
    },
    [permissions, userContext, effectiveScopedAccountId]
  );

  // ============================================
  // Role Helpers
  // ============================================

  const hasRole = useCallback(
    (roleCode: string) => {
      return userContext?.roles.some((r) => r.role_code === roleCode) || false;
    },
    [userContext]
  );

  const getRoles = useCallback(
    () => userContext?.roles || [],
    [userContext]
  );

  // ============================================
  // Group Helpers
  // ============================================

  const isInGroup = useCallback(
    (groupName: string) => {
      return userContext?.groups.includes(groupName) || false;
    },
    [userContext]
  );

  const getGroups = useCallback(
    () => userContext?.groups || [],
    [userContext]
  );

  // ============================================
  // Constraint Helpers
  // ============================================

  const getConstraints = useCallback(
    () => userContext?.aggregated_constraints || {},
    [userContext]
  );

  const checkConstraintAccess = useCallback(
    (context: ConstraintContext) => {
      return checkConstraints(userContext?.aggregated_constraints, context);
    },
    [userContext]
  );

  // ============================================
  // Approval Helpers
  // ============================================

  const requiresApprovalForPublish = useCallback(
    () => userContext?.approval_policy?.requires_approval_for_publish || false,
    [userContext]
  );

  const requiresApprovalForSpend = useCallback(
    (amount: number) => {
      if (!userContext?.approval_policy?.requires_approval_for_spend) return false;
      return amount >= (userContext.approval_policy.spend_threshold || 0);
    },
    [userContext]
  );

  // ============================================
  // Entitlement Helpers
  // ============================================

  const hasEntitlement = useCallback(
    (feature: string) => {
      return userContext?.entitlements?.includes(feature) || false;
    },
    [userContext]
  );

  // ============================================
  // Computed Values
  // ============================================

  const isOrgOwner = useMemo(
    () => Boolean(userContext?.is_org_owner) || hasRole('org_owner'),
    [userContext?.is_org_owner, hasRole]
  );

  const isAccountAdmin = useMemo(
    () => hasRole('account_admin') || hasRole('org_admin') || hasRole('org_owner'),
    [hasRole]
  );

  const currentOrgId = userContext?.organization_id || null;

  const isSubTenantScopeLoading = useMemo(
    () =>
      Boolean(effectiveScopedAccountId) &&
      Boolean(userContext?.user_id) &&
      scopedPermissions === null,
    [effectiveScopedAccountId, userContext?.user_id, scopedPermissions]
  );

  /**
   * Sub-tenant / switched-account scope for backend `member_user_ids`.
   * When the org owner has activated root org context (isRootContextActive) — or hasn't picked
   * any context at all yet (no sub-account, no root, the dashboard's default state) — use the
   * root account ID so that `memberUserIds` is fetched for root account members (enabling
   * aggregate campaign listing and org-level campaigns visible to members). The default state is
   * folded in here so an org owner sees the same org-wide numbers whether they explicitly picked
   * "Organization" or never touched the selector — previously the default silently scoped to just
   * the owner's own data, which looked like a bug (numbers changed only once "Organization" was
   * clicked). Permissions scope (effectiveScopedAccountId) stays null/unaffected by this default,
   * so the owner retains full ["*"] permissions regardless.
   *
   * Once the owner switches into a specific member under root context, effectiveScopedAccountId also
   * resolves to the root account id (see above) so permissions are scoped to that member's role; this
   * listing scope stays aligned with it so the member rewrite of user_id rides the same account
   * aggregate the member would see when logged in directly (X-Active-Account-Id + memberUserIds).
   */
  const effectiveListingSubAccountId = useMemo(() => {
    const isDefaultUnpickedContext = !isRootContextActive && selectedSubAccountId == null;
    if ((isRootContextActive || isDefaultUnpickedContext) && Boolean(userContext?.is_org_owner)) {
      // currentAccountId is set to the real root account id by the "Organization" selector
      // (resolved via listAllAccounts, independent of /me/context) — prefer it. A pure org-owner's
      // /me/context response can have account: null, so defaultAccountIdState/readStoredOwnerAccountId()
      // may never get populated; keep them only as a fallback.
      const rootId = currentAccountId ?? defaultAccountIdState ?? readStoredOwnerAccountId();
      if (rootId != null) return rootId;
    }
    return effectiveScopedAccountId;
  }, [isRootContextActive, selectedSubAccountId, currentAccountId, defaultAccountIdState, effectiveScopedAccountId, userContext?.is_org_owner]);

  // ============================================
  // Context Value
  // ============================================

  const value: RBACContextType = useMemo(
    () => ({
      // State
      userContext,
      isLoading,
      error,
      
      // Root org context
      isRootContextActive,
      setIsRootContextActive,

      // Current scope
      currentAccountId,
      defaultAccountId: defaultAccountIdState,
      currentOrgId,
      selectedSubAccountId,
      selectedSubTenantMemberExternalUserId,
      selectedSubTenantMemberRbacUserId,
      effectiveListingSubAccountId,

      // Actions
      setCurrentAccountId,
      setSelectedSubAccountId,
      setSelectedSubTenantMemberExternalUserId,
      refreshContext,
      
      // Permission helpers
      hasPermission,
      hasAnyPermission,
      canAccess,
      
      // Role helpers
      hasRole,
      getRoles,
      
      // Group helpers
      isInGroup,
      getGroups,
      
      // Constraint helpers
      getConstraints,
      checkConstraintAccess,
      
      // Approval helpers
      requiresApprovalForPublish,
      requiresApprovalForSpend,
      
      // Entitlement helpers
      hasEntitlement,
      
      // Computed values
      isOrgOwner,
      isAccountAdmin,
      permissions,
      isSubTenantScopeLoading,
    }),
    [
      userContext,
      isLoading,
      error,
      isRootContextActive,
      setIsRootContextActive,
      currentAccountId,
      defaultAccountIdState,
      currentOrgId,
      selectedSubAccountId,
      selectedSubTenantMemberExternalUserId,
      selectedSubTenantMemberRbacUserId,
      effectiveListingSubAccountId,
      setCurrentAccountId,
      setSelectedSubAccountId,
      setSelectedSubTenantMemberExternalUserId,
      refreshContext,
      hasPermission,
      hasAnyPermission,
      canAccess,
      hasRole,
      getRoles,
      isInGroup,
      getGroups,
      getConstraints,
      checkConstraintAccess,
      requiresApprovalForPublish,
      requiresApprovalForSpend,
      hasEntitlement,
      isOrgOwner,
      isAccountAdmin,
      permissions,
      isSubTenantScopeLoading,
    ]
  );

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
}

export default RBACProvider;

